/**
 * ⛑️ HELMETS
 *
 * Head armor equipment items (15 total)
 * Level requirements: 5-90 (increments of 5)
 * Stat requirements: STR for heavy helms, INT for magical headgear
 *
 * Level Distribution:
 * - Common (3): Lv 5, 10, 15
 * - Uncommon (3): Lv 20, 25, 30
 * - Rare (3): Lv 35, 40, 45
 * - Epic (3): Lv 50, 55, 60
 * - Legendary (3): Lv 70, 80, 90
 */

export const HELMETS = {
  // ============================================================
  // COMMON HELMETS (3) - Lv 5, 10, 15
  // ============================================================
  leatherCap: {
    id: 'leatherCap',
    name: 'Leather Cap',
    icon: '🧢',
    slot: 'helmet',
    rarity: 'common',
    levelReq: 5,
    requirements: {},
    description: 'Protects your head from light blows.',
    stats: {
      health: 10,
      defense: 1,
    },
  },
  ironHelmet: {
    id: 'ironHelmet',
    name: 'Iron Helmet',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'common',
    levelReq: 10,
    requirements: { STR: 10 },
    description: 'A basic iron helm, dented but functional.',
    stats: {
      health: 8,
      defense: 2,
    },
  },
  woodenMask: {
    id: 'woodenMask',
    name: 'Wooden Mask',
    icon: '🎭',
    slot: 'helmet',
    rarity: 'common',
    levelReq: 15,
    requirements: { INT: 12 },
    description: 'A ceremonial mask that offers minimal protection.',
    stats: {
      health: 12,
      defense: 1,
    },
  },

  // ============================================================
  // UNCOMMON HELMETS (3) - Lv 20, 25, 30
  // ============================================================
  steelHelm: {
    id: 'steelHelm',
    name: 'Steel Helm',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'uncommon',
    levelReq: 20,
    requirements: { STR: 30 },
    description: 'A well-crafted steel helmet with a visor.',
    stats: {
      health: 22,
      defense: 3,
    },
  },
  knightHelmet: {
    id: 'knightHelmet',
    name: 'Knight Helmet',
    icon: '👑',
    slot: 'helmet',
    rarity: 'uncommon',
    levelReq: 25,
    requirements: { STR: 40 },
    description: 'Full-face protection for the noble warrior.',
    stats: {
      health: 25,
      defense: 4,
    },
  },
  arcaneHood: {
    id: 'arcaneHood',
    name: 'Arcane Hood',
    icon: '🧙',
    slot: 'helmet',
    rarity: 'uncommon',
    levelReq: 30,
    requirements: { INT: 45 },
    description: 'A hood imbued with protective enchantments.',
    stats: {
      health: 20,
      defense: 3,
    },
  },

  // ============================================================
  // RARE HELMETS (3) - Lv 35, 40, 45
  // ============================================================
  dragonHelm: {
    id: 'dragonHelm',
    name: 'Dragon Helm',
    icon: '🐲',
    slot: 'helmet',
    rarity: 'rare',
    levelReq: 35,
    requirements: { STR: 60 },
    description: "Shaped like a dragon's head, fearsome and strong.",
    stats: {
      health: 42,
      defense: 6,
    },
  },
  crownOfThrones: {
    id: 'crownOfThrones',
    name: 'Crown of Thrones',
    icon: '👑',
    slot: 'helmet',
    rarity: 'rare',
    levelReq: 40,
    requirements: { STR: 50, INT: 30 },
    description: 'A crown worn by ancient warrior kings.',
    stats: {
      health: 38,
      defense: 5,
    },
  },
  crystalCirclet: {
    id: 'crystalCirclet',
    name: 'Crystal Circlet',
    icon: '💎',
    slot: 'helmet',
    rarity: 'rare',
    levelReq: 45,
    requirements: { INT: 75 },
    description: 'A delicate circlet that shields the mind.',
    stats: {
      health: 45,
      defense: 4,
    },
  },

  // ============================================================
  // EPIC HELMETS (3) - Lv 50, 55, 60
  // ============================================================
  titanHelm: {
    id: 'titanHelm',
    name: 'Titan Helm',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 50,
    requirements: { STR: 100 },
    description: 'Forged by giants, sized for mortals.',
    stats: {
      health: 68,
      defense: 10,
    },
  },
  voidMask: {
    id: 'voidMask',
    name: 'Void Mask',
    icon: '👹',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 55,
    requirements: { INT: 90, STR: 40 },
    description: 'Those who wear it see into the abyss.',
    stats: {
      health: 72,
      defense: 9,
    },
  },
  celestialCrown: {
    id: 'celestialCrown',
    name: 'Celestial Crown',
    icon: '👑',
    slot: 'helmet',
    rarity: 'epic',
    levelReq: 60,
    requirements: { INT: 110 },
    description: 'A crown blessed by the stars above.',
    stats: {
      health: 75,
      defense: 8,
    },
  },

  // ============================================================
  // LEGENDARY HELMETS (3) - Lv 70, 80, 90
  // ============================================================
  helmOfEternity: {
    id: 'helmOfEternity',
    name: 'Helm of Eternity',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 70,
    requirements: { STR: 140 },
    description: 'Worn by immortal heroes throughout time.',
    stats: {
      health: 125,
      defense: 17,
    },
  },
  crownOfGods: {
    id: 'crownOfGods',
    name: 'Crown of Gods',
    icon: '👑',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 80,
    requirements: { STR: 100, INT: 100 },
    description: 'Only the truly divine may wear this crown.',
    stats: {
      health: 135,
      defense: 15,
    },
  },
  infinityMask: {
    id: 'infinityMask',
    name: 'Infinity Mask',
    icon: '💎',
    slot: 'helmet',
    rarity: 'legendary',
    levelReq: 90,
    requirements: { INT: 180 },
    description: 'See all possible futures and pasts.',
    stats: {
      health: 140,
      defense: 14,
    },
  },
};
